import { c as _c } from "react/compiler-runtime";
import type { MouseEvent } from "react";
import { CirclePause, Loader2 } from "lucide-react";
import { Button } from "@repo/blacksmith-ui/button";
import { cn } from "@repo/blacksmith-ui/cn";
import { STATUS_COLORS } from "@/components/filters/StatusFilter";
export const KEEP_GOING_ROUNDS = 3;
interface LoopLimitStripProps {
  headline: string;
  /** Trails the compact headline only when the strip has room, e.g. `after 3 rounds`. */
  headlineMeta?: string;
  detail?: string;
  onKeepGoing?: (event: MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  actionLabel?: string;
  pending: boolean;
  continued: boolean;
  continuedHeadline: string;
  continuedDetail?: string;
  testId: string;
  tabIndex?: number;
  className?: string;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  /** A divided footer row inside a card; the detail line is not rendered. */
  compact?: boolean;
}
export function LoopLimitStrip(t0) {
  const $ = _c(47);
  const {
    headline,
    headlineMeta,
    detail,
    onKeepGoing,
    actionLabel: t1,
    pending,
    continued,
    continuedHeadline,
    continuedDetail,
    testId,
    tabIndex,
    className,
    onClick,
    compact: t2
  } = t0;
  const actionLabel = t1 === undefined ? "Keep going" : t1;
  const compact = t2 === undefined ? false : t2;
  const displayedHeadline = continued ? continuedHeadline : headline;
  const displayedDetail = continued ? continuedDetail : detail;
  let t3;
  if ($[0] !== actionLabel || $[1] !== compact || $[2] !== continued || $[3] !== onKeepGoing || $[4] !== pending || $[5] !== tabIndex) {
    t3 = !continued && onKeepGoing ? <Button type="button" disabled={pending} onClick={onKeepGoing} tabIndex={tabIndex} variant="secondary" size={compact ? "xs" : undefined} className={compact ? "shrink-0" : undefined}>{pending && !compact ? <Loader2 className="mr-1 size-3 animate-spin" /> : null}{actionLabel}</Button> : null;
    $[0] = actionLabel;
    $[1] = compact;
    $[2] = continued;
    $[3] = onKeepGoing;
    $[4] = pending;
    $[5] = tabIndex;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const action = t3;
  if (compact) {
    let t4;
    if ($[7] !== className) {
      t4 = cn("@container flex min-w-0 items-center justify-between gap-2 border-t border-[var(--color-border)] pt-1.5", className);
      $[7] = className;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] !== continued || $[10] !== pending) {
      t5 = pending && !continued ? <Loader2 aria-hidden={true} className="size-3 shrink-0 animate-spin text-neutral-500 dark:text-neutral-400" /> : <CirclePause aria-hidden={true} className="size-3 shrink-0 text-neutral-500 dark:text-neutral-400" />;
      $[9] = continued;
      $[10] = pending;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    let t6;
    if ($[12] !== continued || $[13] !== headlineMeta) {
      t6 = headlineMeta && !continued ? <><span className="sr-only"> {headlineMeta}</span><span aria-hidden={true} className="hidden type-body-xs-regular text-neutral-500 @min-[20rem]:inline dark:text-neutral-400">{" "}{headlineMeta}</span></> : null;
      $[12] = continued;
      $[13] = headlineMeta;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    let t7;
    if ($[15] !== displayedHeadline || $[16] !== t6) {
      t7 = <p className="truncate type-body-xs-medium text-neutral-900 dark:text-neutral-100">{displayedHeadline}{t6}</p>;
      $[15] = displayedHeadline;
      $[16] = t6;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    let t8;
    if ($[18] !== t5 || $[19] !== t7) {
      t8 = <div className="flex h-6 min-w-0 items-center gap-1.5">{t5}{t7}</div>;
      $[18] = t5;
      $[19] = t7;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    let t9;
    if ($[21] !== action || $[22] !== onClick || $[23] !== t4 || $[24] !== t8 || $[25] !== testId) {
      t9 = <div className={t4} data-testid={testId} onClick={onClick}>{t8}{action}</div>;
      $[21] = action;
      $[22] = onClick;
      $[23] = t4;
      $[24] = t8;
      $[25] = testId;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    return t9;
  }
  let t4;
  if ($[27] !== className) {
    t4 = cn("flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2", className);
    $[27] = className;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  const t5 = continued ? "var(--color-border)" : STATUS_COLORS.queued;
  let t6;
  if ($[29] !== t5) {
    t6 = <span aria-hidden={true} className="mt-[7px] size-1.5 shrink-0 rounded-full" style={{
      backgroundColor: t5
    }} />;
    $[29] = t5;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  let t7;
  if ($[31] !== displayedHeadline) {
    t7 = <p className="type-body-s-medium text-neutral-900 dark:text-neutral-100">{displayedHeadline}</p>;
    $[31] = displayedHeadline;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== displayedDetail) {
    t8 = displayedDetail ? <p className="type-body-xs-regular text-neutral-500 dark:text-neutral-400">{displayedDetail}</p> : null;
    $[33] = displayedDetail;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== t7 || $[36] !== t8) {
    t9 = <div className="min-w-0">{t7}{t8}</div>;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[38] !== t6 || $[39] !== t9) {
    t10 = <div className="flex min-w-0 items-start gap-2">{t6}{t9}</div>;
    $[38] = t6;
    $[39] = t9;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== action || $[42] !== onClick || $[43] !== t10 || $[44] !== t4 || $[45] !== testId) {
    t11 = <div className={t4} data-testid={testId} onClick={onClick}>{t10}{action}</div>;
    $[41] = action;
    $[42] = onClick;
    $[43] = t10;
    $[44] = t4;
    $[45] = testId;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  return t11;
}